import { useEffect, useMemo, useState } from "react";
import { AirplaneTilt, ArrowCounterClockwise, Plus, Trash, Warning, GasPump } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { SearchableSelect } from "./SearchableSelect";
import { autopilotAPI, glAccountsAPI, vendorsAPI, settingsAPI } from "../lib/api";
import { formatDate } from "../lib/formatDate";
import { buildGlItems } from "../lib/glAccounts";

const isOverheadAccount = (a) => !["Bank", "Credit Card", "CreditCard"].includes(a.account_type);

const AddRuleForm = ({ vendorItems, glItems, onAdd }) => {
  const [form, setForm] = useState({ vendor_qbo_id: "", gl_account_id: "", max_amount: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.vendor_qbo_id || !form.gl_account_id || !(parseFloat(form.max_amount) > 0)) {
      return toast.error("Pick a vendor, an overhead account and a max amount");
    }
    setSaving(true);
    const ok = await onAdd({ ...form, max_amount: parseFloat(form.max_amount) });
    setSaving(false);
    if (ok) setForm({ vendor_qbo_id: "", gl_account_id: "", max_amount: "" });
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end p-3 bg-zinc-50 border border-zinc-200 rounded-sm" data-testid="autopilot-add-form">
      <div className="sm:col-span-4">
        <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Vendor</Label>
        <SearchableSelect value={form.vendor_qbo_id} onChange={(v) => setForm({ ...form, vendor_qbo_id: v })}
          items={vendorItems} placeholder="QuickBooks vendor" size="sm" testId="autopilot-add-vendor" />
      </div>
      <div className="sm:col-span-4">
        <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Overhead account</Label>
        <SearchableSelect value={form.gl_account_id} onChange={(v) => setForm({ ...form, gl_account_id: v })}
          items={glItems} placeholder="Overhead account" size="sm" wrap testId="autopilot-add-gl" />
      </div>
      <div className="sm:col-span-2">
        <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Max per swipe</Label>
        <Input type="number" min="0" step="0.01" value={form.max_amount} placeholder="$"
          onChange={(e) => setForm({ ...form, max_amount: e.target.value })}
          className="h-8 rounded-sm text-xs font-mono" data-testid="autopilot-add-max" />
      </div>
      <Button size="sm" onClick={submit} disabled={saving} data-testid="autopilot-add-btn"
        className="sm:col-span-2 h-8 rounded-sm text-xs gap-1 bg-[#002FA7] hover:bg-[#002280] text-white">
        <Plus size={12} weight="bold" /> Add rule
      </Button>
    </div>
  );
};

const RuleRow = ({ rule, glById, glAccounts, onPatch, onDelete }) => {
  const [max, setMax] = useState(String(rule.max_amount));
  useEffect(() => setMax(String(rule.max_amount)), [rule.max_amount]);
  const glItems = useMemo(
    () => buildGlItems(glAccounts, { filter: isOverheadAccount, selectedIds: [rule.gl_account_id] }),
    [glAccounts, rule.gl_account_id],
  );
  const gl = glById[rule.gl_account_id];
  const glProblem = !gl ? "Account not found — sync QuickBooks or pick another" : gl.active === false ? "Account is inactive in QuickBooks" : "";
  const saveMax = () => {
    const v = parseFloat(max);
    if (v > 0 && v !== rule.max_amount) onPatch(rule.id, { max_amount: v });
    else setMax(String(rule.max_amount));
  };
  return (
    <div className={`px-3 py-2.5 grid grid-cols-1 sm:grid-cols-12 gap-2 sm:items-center ${rule.active ? "" : "opacity-50"}`} data-testid={`autopilot-rule-${rule.id}`}>
      <div className="sm:col-span-3 min-w-0">
        <p className="text-sm font-medium text-zinc-800 truncate flex items-center gap-1.5">
          {rule.match_fuel_vendors && <GasPump size={13} className="text-sky-600 flex-shrink-0" />}
          {rule.vendor_name}
        </p>
        <p className="text-[10px] text-zinc-400">{rule.match_fuel_vendors ? "Catch-all · vendor rules win" : `QBO vendor #${rule.vendor_qbo_id}`}</p>
      </div>
      <div className="sm:col-span-4 min-w-0">
        <SearchableSelect value={rule.gl_account_id} onChange={(v) => onPatch(rule.id, { gl_account_id: v })}
          items={glItems} placeholder={rule.gl_account_name || (rule.gl_account_number ? `#${rule.gl_account_number}` : "Pick account")} size="sm" wrap testId={`autopilot-rule-gl-${rule.id}`} />
        {glProblem && (
          <p className="text-[10px] text-amber-700 mt-0.5 flex items-center gap-1" data-testid={`autopilot-rule-gl-warning-${rule.id}`}>
            <Warning size={10} weight="bold" /> {glProblem}
          </p>
        )}
      </div>
      <div className="sm:col-span-2">
        <Input type="number" min="0" step="0.01" value={max} onChange={(e) => setMax(e.target.value)} onBlur={saveMax}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          className="h-8 rounded-sm text-xs font-mono" data-testid={`autopilot-rule-max-${rule.id}`} />
      </div>
      <div className="sm:col-span-1 text-[10px] text-zinc-500" data-testid={`autopilot-rule-hits-${rule.id}`}>
        {rule.hits || 0} hits{rule.last_hit_at ? <span className="block text-zinc-400">{formatDate(rule.last_hit_at.slice(0, 10))}</span> : null}
      </div>
      <div className="sm:col-span-2 flex items-center justify-end gap-2">
        <Switch checked={!!rule.active} onCheckedChange={(v) => onPatch(rule.id, { active: v })} data-testid={`autopilot-rule-active-${rule.id}`} />
        <Button size="sm" variant="ghost" onClick={() => onDelete(rule)} data-testid={`autopilot-rule-delete-${rule.id}`}
          className="h-7 w-7 p-0 text-zinc-400 hover:text-red-600"><Trash size={13} /></Button>
      </div>
    </div>
  );
};

export const AutopilotRulesPanel = () => {
  const [rules, setRules] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [glAccounts, setGlAccounts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [r, g, v] = await Promise.all([autopilotAPI.list(), glAccountsAPI.list(), vendorsAPI.listAll()]);
      setRules(r.data.rules); setEnabled(r.data.enabled);
      setGlAccounts(g.data.accounts || []);
      setVendors((v.data.vendors || v.data || []).filter((x) => x.qbo_id));
    } catch (e) { toast.error("Couldn't load Autopilot rules"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const glById = useMemo(() => Object.fromEntries(glAccounts.map((a) => [a.id, a])), [glAccounts]);
  const glItems = useMemo(() => buildGlItems(glAccounts, { filter: isOverheadAccount }), [glAccounts]);
  const vendorItems = useMemo(() => vendors.map((v) => ({ value: v.qbo_id, label: v.name })), [vendors]);

  const toggleMaster = async (v) => {
    try { await settingsAPI.update({ autopilot_enabled: v }); setEnabled(v); toast.success(v ? "Autopilot is on" : "Autopilot is off"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const patch = async (id, data) => {
    try { const { data: up } = await autopilotAPI.update(id, data); setRules((p) => p.map((r) => (r.id === id ? up : r))); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to save rule"); }
  };
  const add = async (data) => {
    try { await autopilotAPI.create(data); toast.success("Rule added"); load(); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to add rule"); return false; }
  };
  const remove = async (rule) => {
    if (!window.confirm(`Delete the Autopilot rule for ${rule.vendor_name}?`)) return;
    try { await autopilotAPI.delete(rule.id); setRules((p) => p.filter((r) => r.id !== rule.id)); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const restore = async () => {
    if (!window.confirm("Restore the default rules? Missing defaults are re-added and existing defaults get their original account and max back. Your own rules stay untouched.")) return;
    try { const { data } = await autopilotAPI.restoreDefaults(); toast.success(`Restored — ${data.created} added, ${data.reset} reset`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="p-6 text-sm text-zinc-400 animate-pulse">Loading Autopilot…</div>;

  return (
    <div className="space-y-4" data-testid="autopilot-panel">
      <div className="bg-white border border-zinc-200 rounded-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <AirplaneTilt size={28} weight="duotone" className="text-sky-600 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-bold text-zinc-900">Autopilot {enabled ? "is on" : "is off"}</p>
          <p className="text-xs text-zinc-500 leading-snug">
            When a card swipe settles from a vendor below and is at or under the max, it's coded to the overhead account,
            approved and pushed to QuickBooks straight away. The cardholder just gets a nudge to add the receipt.
          </p>
        </div>
        <Switch checked={enabled} onCheckedChange={toggleMaster} data-testid="autopilot-master-switch" />
      </div>
      <AddRuleForm vendorItems={vendorItems} glItems={glItems} onAdd={add} />
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="hidden sm:grid sm:grid-cols-12 gap-2 px-3 py-2 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-400">
          <div className="col-span-3">Vendor</div><div className="col-span-4">Overhead account</div>
          <div className="col-span-2">Max per swipe</div><div className="col-span-1">Used</div><div className="col-span-2 text-right">Active</div>
        </div>
        <div className="divide-y divide-zinc-100" data-testid="autopilot-rules-list">
          {rules.length === 0 && <p className="px-3 py-6 text-center text-xs text-zinc-400" data-testid="autopilot-empty">No rules yet.</p>}
          {rules.map((r) => <RuleRow key={r.id} rule={r} glById={glById} glAccounts={glAccounts} onPatch={patch} onDelete={remove} />)}
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={restore} className="rounded-sm text-xs gap-1" data-testid="autopilot-restore-defaults-btn">
        <ArrowCounterClockwise size={12} /> Restore default rules
      </Button>
    </div>
  );
};
